import React from 'react';
import { useIntl } from 'react-intl';
import { getIn, useFormikContext } from 'formik';

import Grid from '@mui/material/Grid';

import { isEmpty, isFieldVisible, isNil, startCase } from 'app/utils';
import { InputFieldFactory } from 'app/components';
import { getSelectionItems } from 'app/components/FormikField/Factory/SettingsFieldFactory';
import { TranslationKeys } from 'app/translations';
import type { ApiField, FieldCondition } from 'app/types';

type UpdateCredentialFormProps = {
  fields: ApiField[];
};

const remapConditionField = (condition: FieldCondition, packageType?: string): FieldCondition => {
  const prefix = `packages.${packageType}.`;
  const stripPrefix = (fieldPath: string) =>
    packageType && fieldPath.startsWith(prefix) ? fieldPath.slice(prefix.length) : fieldPath;

  if ('and' in condition) {
    return { and: condition.and.map(c => remapConditionField(c, packageType)) };
  }
  if ('or' in condition) {
    return { or: condition.or.map(c => remapConditionField(c, packageType)) };
  }
  if ('not' in condition) {
    return { not: remapConditionField(condition.not, packageType) };
  }

  return { ...condition, field: stripPrefix(condition.field) };
};

type CredentialFieldProps = {
  field: ApiField;
  fieldItems?: any[];
};

const CredentialField = ({ field, fieldItems }: CredentialFieldProps) => {
  const intl = useIntl();
  const { values, setFieldValue } = useFormikContext<any>();

  const showIf = React.useMemo(
    () => (field.fieldOptions?.showIf ? remapConditionField(field.fieldOptions.showIf, values.packageType) : undefined),
    [field.fieldOptions?.showIf, values.packageType],
  );

  const visible = isFieldVisible({ showIf }, values);

  React.useEffect(() => {
    if (!visible) {
      const currentValue = getIn(values, field.fieldName);
      if (!isNil(currentValue)) {
        setFieldValue(field.fieldName, null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, field.fieldName]);

  if (!visible) {
    return null;
  }

  return (
    <Grid item xs={12}>
      <InputFieldFactory
        disabled={field.fieldOptions?.disabled ?? false}
        field={{
          name: field.fieldName,
          required: field.fieldOptions?.required ?? false,
          label: intl.formatMessage({ id: `onboarding.credentials.new.${field.fieldName}` }),
          type: field.fieldType,
          options: field.fieldOptions,
          items: fieldItems,
        }}
      />
    </Grid>
  );
};

export const UpdateCredentialForm = ({ fields }: UpdateCredentialFormProps) => {
  const intl = useIntl();

  const { initialValues, values } = useFormikContext<any>();

  const ownerSelectItems = React.useMemo(() => {
    return [
      {
        element: initialValues.ownerName,
        value: initialValues.ownerId,
      },
    ];
  }, [initialValues.ownerId, initialValues.ownerName]);

  const packageTypeSelectItems = React.useMemo(() => {
    return [
      {
        element: startCase(initialValues.packageType?.replace('-', ' ')),
        value: initialValues.packageType,
      },
    ];
  }, [initialValues.packageType]);

  const hasSelectedPackageTypeAndHaveFields = values.packageType && !isEmpty(fields);

  return (
    <>
      <Grid container spacing={2} marginBottom={2}>
        <Grid item xs={12}>
          <InputFieldFactory
            disabled
            field={{
              type: 'select',
              name: 'ownerId',
              label: intl.formatMessage({ id: TranslationKeys.credentials_owner }),
              items: ownerSelectItems,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <InputFieldFactory
            disabled
            field={{
              type: 'select',
              name: 'packageType',
              label: intl.formatMessage({ id: TranslationKeys.credentials_packageType }),
              items: packageTypeSelectItems,
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        {hasSelectedPackageTypeAndHaveFields &&
          fields.map(field => (
            <CredentialField
              key={field.fieldName}
              field={field}
              fieldItems={field.fieldType === 'select' ? getSelectionItems(intl, field) : undefined}
            />
          ))}
      </Grid>
    </>
  );
};
